/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useRef, useState, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import PropTypes from 'prop-types';
import { useIntersect } from 'sarsaparilla';
import { getLayersPerService } from '../layers/layers';
import { getPopupContentByService } from '../helpers/mapPopup';
import {
    setMapCanvasAttributes,
    cursorToDefault,
    cursorToPointer,
    fitMapBounds,
    addMapSources,
    updateMapPosition,
    setMapSource,
    trackIsMapExtended,
} from '../helpers/common';
import {
    addMapControls,
    addMapSpecialControls,
    getControlsPerService,
    getSpecialControlsPerService,
} from '../controls/controls';
import { tripPlannerSources as sources } from '../constants';
import LoadingSpinner from '../components/LoadingSpinner';

function TripPlannerMap(props) {
    const map = useRef(null);
    const defaultPopup = useRef();
    const specialMapControls = useRef();
    const layerSetup = useRef(
        getLayersPerService({
            service: props.service,
            source: Object.keys(props.geojsons || []).map((geo) => ({
                name: props.geojsons[geo].sourceName,
                isClustered: props.geojsons[geo].isClustered,
            })),
        })
    );
    const basicControls = useRef(null);
    const basicControlsList = useRef(getControlsPerService(props.service));

    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [isMapExtended, setIsMapExtended] = useState(false);

    const [ref, entry] = useIntersect({ rootMargin: '100px' });

    const deselectFeature = () => {
        if (defaultPopup.current) {
            defaultPopup.current.remove();
            defaultPopup.current = null;
        }
    };

    const setSource = (sourceName, data) => {
        setMapSource({ map, sourceName, data });
    };

    useEffect(() => {
        if (!map.current || !basicControlsList.current?.gesture) return;
        map.current._cooperativeGestures = isMapExtended
            ? false
            : basicControlsList.current?.gesture;
    }, [isMapExtended]);
    useEffect(() => {
        setSource(sources.lineRoute, props.lineRoute);
        fitMapBounds(map, props.lineRoute);
    }, [props.lineRoute, isMapLoaded]);
    useEffect(
        () => setSource(sources.polygonRoute, props.polygonRoute),
        [props.polygonRoute, isMapLoaded]
    );
    useEffect(
        () => setSource(sources.recreation, props.recreation),
        [props.recreation, isMapLoaded]
    );
    useEffect(
        () => setSource(sources.tripDestinations, props.tripDestination),
        [props.tripDestination, isMapLoaded]
    );
    useEffect(
        () => setSource(sources.tripItems, props.tripItems),
        [props.tripItems, isMapLoaded]
    );

    useEffect(() => {
        if (!props.center || !map.current) return;
        updateMapPosition({
            map,
            center: props.center,
            zoom: props.zoom,
            maxZoom: props.zoom,
        });
    }, [props.center]);

    const setPopup = (feature) => {
        deselectFeature();
        if (!feature || !map.current) return;

        const popup = new mapboxgl.Popup({
            closeButton: false,
            focusAfterOpen: false,
            maxWidth: 'none',
        });

        const popupContent = getPopupContentByService(feature, props.service);
        if (!popupContent || !popup) return;

        popup
            .setDOMContent(popupContent)
            .setLngLat(feature.geometry?.coordinates)
            .addTo(map.current);

        map.current.panBy([0, 0]);

        defaultPopup.current = popup;
        if (props.selectedEvent) props.selectedEvent(feature.properties?.featureId);
    };

    useEffect(() => {
        if (!props.selectedFeature) deselectFeature();
        if (
            props.selectedFeature?.asset ||
            props.selectedFeature?.properties?.isSearchDestination
        ) {
            setSelectedFeature(props.selectedFeature);
            setPopup(props.selectedFeature);
        }
    }, [props.selectedFeature]);

    useEffect(() => {
        if (!selectedFeature) {
            if (props.setSelectedFeature) props.setSelectedFeature(selectedFeature);
            return;
        }

        const selectableSources = [
            sources.tripDestinations,
            sources.tripItems,
            sources.recreation,
        ];
        const isSelectableSource = selectableSources.includes(selectedFeature.source);
        if (!isSelectableSource) return;

        if (selectedFeature.layer.id === 'tripDestinationsPointsLayer') {
            setPopup(selectedFeature);
        } else if (props.setSelectedFeature) props.setSelectedFeature(selectedFeature);
    }, [selectedFeature]);

    const addEventsToLayers = () => {
        const layers = layerSetup.current?.layers || [];

        layers.forEach((layer) => {
            map.current.on('mouseleave', layer.id, () => cursorToDefault(map));
            map.current.on('mouseenter', layer.id, () => cursorToPointer(map));
        });
    };

    const addMapLayers = () => {
        if (!map.current) return;
        const layers = layerSetup.current?.layers || [];

        layers.forEach((layer) => {
            if (layer) map.current.addLayer(layer);
        });
    };

    const getCurrentZoom = () => {
        if (!map.current) return null;
        return map.current.getZoom();
    };

    const addMapEvents = () => {
        map.current.on('load', () => {
            setIsMapLoaded(true);
            addMapSources(map, props.geojsons);
            addMapLayers();
            addEventsToLayers();
        });

        // Track double click
        let clicks = 0;

        map.current.on('click', (e) => {
            const point = e.point;
            clicks = 0;
            setTimeout(() => {
                if (clicks) return;
                const features = map.current.queryRenderedFeatures(point, {
                    layers: layerSetup.current?.layers
                        .filter((layer) => !layer?.ignoreSelection)
                        .map((layer) => layer?.id),
                });

                const feature = features?.length ? features[0] : null;
                setSelectedFeature(feature);
            }, 250);
        });

        map.current.on('dblclick', () => {
            clicks += 1;
        });

        map.current.on('moveend', (e) => {
            specialMapControls.current.legend?.updateGlobalContent({
                maxZoom: 8,
                currentZoom: getCurrentZoom(),
            });
            if (!e?.setCenter && props.onMapMoveEnd) props.onMapMoveEnd(map.current);
        });

        map.current.doubleClickZoom.enable();
        map.current.dragPan.enable();
    };

    const removeMapEvents = () => {
        if (props.config?.disableMapScroll) map.current.scrollZoom.disable();
    };

    const initMap = () => {
        if (!map.current) return;
        setMapCanvasAttributes();

        basicControls.current = addMapControls(map, basicControlsList.current);
        if (basicControlsList.current?.gesture) {
            trackIsMapExtended(basicControls.current, setIsMapExtended);
        }

        const specialControlParams = {
            ...getSpecialControlsPerService(props.service),
            ...props.config?.mapSpecialControls,
        };
        specialControlParams.searchLayers =
            !props.config?.mapSpecialControls?.ignoreLayer;
        specialMapControls.current = addMapSpecialControls(map, specialControlParams);

        addMapEvents();
        removeMapEvents();
    };

    useEffect(() => {
        if (!map.current && entry?.isIntersecting) {
            mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN;
            map.current = new mapboxgl.Map({
                container: props.mapContainer,
                style: process.env.MAPBOX_STYLE,
                center: props.center,
                zoom: props.zoom,
                attributionControl: false,
                cooperativeGestures: basicControlsList.current?.gesture,
                preserveDrawingBuffer: true,
                pitch: 0,
                bearing: 0,
            });
            initMap();
        }
    }, [entry?.isIntersecting]);

    return <LoadingSpinner mapRef={ref} isMapLoaded={isMapLoaded} />;
}

TripPlannerMap.propTypes = {
    geojsons: PropTypes.object,
    config: PropTypes.object,
    lineRoute: PropTypes.object,
    polygonRoute: PropTypes.object,
    recreation: PropTypes.object,
    tripDestination: PropTypes.object,
    tripItems: PropTypes.object,
    mapContainer: PropTypes.string,
    center: PropTypes.arrayOf(PropTypes.number),
    zoom: PropTypes.number,
    service: PropTypes.string,
    setSelectedFeature: PropTypes.func,
    selectedFeature: PropTypes.object,
    onMapMoveEnd: PropTypes.func,
    selectedEvent: PropTypes.func,
    featureName: PropTypes.string,
};

export default TripPlannerMap;

// cSpell:ignore geojsons, moveend
